import { FieldError, FieldLabel, Input, Notice, useToast } from '@speaking-coach/ui'
import { FormalNameFields } from '../common/identity-fields.js'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import { queryKeys } from '../api/query-keys.js'
import { FormError, formMessage, messageFor, resolveFailure, type ResolvedFailure } from '../common/field-errors.js'
import {
  OPERATOR_FORM_PAGE,
  OPERATOR_PAGE_MAIN,
} from '../common/page-layout.js'
import { ProvisioningFormActions } from '../common/provisioning-form-actions.js'
import { SubmitButton } from '../common/save-button.js'
import { UnsavedChangesGuard, useLeaveAfterSave } from '../common/unsaved-changes.js'
import { PageHeader } from '../components/page-header.js'
import { createCoooriAdminSchema, type CreateCoooriAdminForm } from './admin-schema.js'
import {
  COOORI_ADMIN_ERROR_FIELD,
  coooriAdminsApi,
  messageForCoooriAdminError,
  type CoooriAdminField,
} from './api.js'

export function CreateCoooriAdminPage(): JSX.Element {
  const queryClient = useQueryClient()
  const { leaving, leave } = useLeaveAfterSave()
  const { toast } = useToast()
  const [failure, setFailure] = useState<ResolvedFailure<CoooriAdminField> | null>(null)
  const form = useForm<CreateCoooriAdminForm>({
    resolver: zodResolver(createCoooriAdminSchema),
    mode: 'onChange',
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
    },
  })

  const create = useMutation({
    mutationFn: (values: CreateCoooriAdminForm) =>
      coooriAdminsApi.create({
        email: values.email.trim(),
        firstName: values.firstName,
        lastName: values.lastName,
      }),
    onSuccess: (admin) => {
      setFailure(null)
      void queryClient.invalidateQueries({ queryKey: queryKeys.coooriAdminLists })
      toast({ variant: 'success', description: `Invitation queued for ${admin.name}.` })
      leave('/admin/admins')
    },
    onError: (error) => {
      setFailure(
        resolveFailure(error, COOORI_ADMIN_ERROR_FIELD, messageForCoooriAdminError),
      )
    },
  })

  const onSubmit = form.handleSubmit((values) => {
    if (create.isPending) return
    setFailure(null)
    create.mutate(values)
  })
  const emailError = form.formState.errors.email?.message
  const serverEmailError = messageFor(failure, 'email')

  return (
    <main className={OPERATOR_PAGE_MAIN}>
      <div className={OPERATOR_FORM_PAGE}>
        <PageHeader title="Invite Cooori admin" />

        <section className="rounded-token-card bg-surface-card p-6">
          <Notice variant="neutral">
            <p className="font-medium text-text-primary">
              What does this create?
            </p>
            <p className="mt-0.5">
              The recipient chooses their own password from a seven-day link. They are
              created only as a Cooori admin—not as a student or HR user.
            </p>
          </Notice>

          <form
            aria-label="Invite Cooori admin"
            className="mt-6"
            noValidate
            onSubmit={onSubmit}
          >
            <UnsavedChangesGuard
              when={form.formState.isDirty && !leaving}
              name="new Cooori admin"
            />
            <FormError message={formMessage(failure)} />

            <div className="space-y-4">
              <FormalNameFields
                firstName={form.register('firstName')}
                lastName={form.register('lastName')}
                errors={form.formState.errors}
              />

              <div>
                <FieldLabel htmlFor="admin-email" label="Email" />
                <Input
                  id="admin-email"
                  type="email"
                  autoComplete="email"
                  {...form.register('email')}
                  aria-invalid={emailError !== undefined || serverEmailError !== null}
                />
                <FieldError message={serverEmailError ?? emailError ?? null} />
              </div>
            </div>

            <ProvisioningFormActions cancelTo="/admin/admins">
              <SubmitButton
                pending={create.isPending}
                pendingLabel="Queuing invitation…"
                disabled={!form.formState.isValid}
              >
                Send invitation
              </SubmitButton>
            </ProvisioningFormActions>
          </form>
        </section>
      </div>
    </main>
  )
}
